import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowUpRight,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  HardHat,
  LogIn,
  User2Icon,
} from 'lucide-react';
import logoApp from '../../assets/images/logo.png';
import stepMasuk from '../../assets/images/landing/step-01-masuk.png';
import stepPenawaran from '../../assets/images/landing/step-02-penawaran.png';
import stepPantau from '../../assets/images/landing/step-03-pantau.png';
import mitrabg from '../../assets/images/landing/MItraAsset1.png';
import ownerbg from '../../assets/images/landing/OwnerAsset1.png';
import ctaDownload from '../../assets/images/landing/CTADownload3.png';
import heroImg from '../../assets/images/landing/HeroImgFinal.png';
import heroImgMobile from '../../assets/images/landing/HeroImgFinalMobile.jpg';
import Fitur1 from '../../assets/images/landing/Fitur1.png';
import Fitur2 from '../../assets/images/landing/Fitur2.png';
import Fitur3 from '../../assets/images/landing/Fitur3.png';
import Fitur4 from '../../assets/images/landing/Fitur4.png';
import Fitur5 from '../../assets/images/landing/Fitur5.png';
import Fitur6 from '../../assets/images/landing/Fitur6.png';

const MARKETPLACE_URL =
  import.meta.env.VITE_API_BASE_URL?.replace(/\/api\/?$/, '') ||
  'https://indoteknisi.net';

const PLAY_STORE_URL =
  import.meta.env.VITE_PLAYSTORE_URL ||
  'https://play.google.com/store/apps/details?id=id.indoteknisi.com';

const APP_STORE_URL =
  import.meta.env.VITE_APPSTORE_URL ||
  'https://apps.apple.com/id/app/indoteknisi/id6760164008';

/** Siap diperluas — slider menampilkan beberapa kartu per layar.
 *  Gambar sementara memutar asset yang ada (nanti diganti ilustrasi khusus).
 */
const FEATURE_SLIDES = [
  {
    title: 'Pasang proyek',
    body: 'Owner menerbitkan kebutuhan pekerjaan lengkap dengan lokasi, anggaran, dan target waktu.',
    image: Fitur1,
    imageAlt: 'Ilustrasi memasang proyek di Portal Indoteknisi',
  },
  {
    title: 'Cari peluang proyek',
    body: 'Mitra menemukan proyek yang cocok di bursa, menyimpan favorit, lalu menindaklanjuti peluang terbaik.',
    image: Fitur2,
    imageAlt: 'Ilustrasi mitra mencari peluang proyek',
  },
  {
    title: 'Kirim & terima penawaran',
    body: 'Mitra mengirim penawaran resmi; owner membandingkan dan memilih mitra — bukan sekadar chat perkiraan harga.',
    image: Fitur3,
    imageAlt: 'Ilustrasi penawaran resmi antara owner dan mitra',
  },
  {
    title: 'Chat & notifikasi',
    body: 'Diskusi dan pemberitahuan tetap di portal, sehingga koordinasi tidak tersebar di banyak channel.',
    image: Fitur6,
    imageAlt: 'Ilustrasi chat dan notifikasi portal',
  },
  {
    title: 'Manajemen & validasi kerja',
    body: 'Setelah deal, susun rencana, update progres lapangan, lalu validasi pekerjaan dalam satu workspace.',
    image: Fitur4,
    imageAlt: 'Ilustrasi manajemen dan validasi pekerjaan',
  },
  {
    title: 'Pantau pengerjaan & termin',
    body: 'Pantau status proyek dan alur pembayaran termin tanpa meninggalkan Portal Proyek Indoteknisi.',
    image: Fitur5,
    imageAlt: 'Ilustrasi pantau pengerjaan dan termin',
  },
];

const STEPS = [
  {
    step: '01',
    title: 'Masuk dengan akun Indoteknisi',
    body: 'Satu akun untuk website, aplikasi, dan portal proyek. Tidak ada pendaftaran terpisah.',
    image: stepMasuk,
    imageAlt: 'Ilustrasi masuk aman ke Portal Proyek Indoteknisi',
  },
  {
    step: '02',
    title: 'Owner pasang, mitra menawar',
    body: 'Owner membuka proyek baru. Mitra yang relevan mengirim penawaran lengkap dengan estimasi.',
    image: stepPenawaran,
    imageAlt: 'Ilustrasi mitra meninjau proyek dan mengirim penawaran',
  },
  {
    step: '03',
    title: 'Sepakat, lalu kerjakan',
    body: 'Setelah owner memilih mitra, progres pekerjaan dan pesan tetap berjalan di portal ini.',
    image: stepPantau,
    imageAlt: 'Ilustrasi mitra memantau progres pengerjaan proyek',
  },
];

function PrimaryButton({
  children,
  className = '',
  to,
  href,
}: {
  children: ReactNode;
  className?: string;
  to?: string;
  href?: string;
}) {
  const classes =
    'inline-flex items-center justify-center gap-2 rounded-md bg-[#3451d1] px-6 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-[#2176CC] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#4A6CF7]/45 ' +
    className;

  if (to) {
    return (
      <Link to={to} className={classes}>
        {children}
      </Link>
    );
  }

  return (
    <a href={href} className={classes}>
      {children}
    </a>
  );
}

function usePerView() {
  const [perView, setPerView] = useState(1);

  useEffect(() => {
    const update = () => {
      const w = window.innerWidth;
      if (w >= 1024) setPerView(3);
      else if (w >= 768) setPerView(2);
      else setPerView(1);
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  return perView;
}

function FeatureSlider({
  slides,
}: {
  slides: typeof FEATURE_SLIDES;
}) {
  const perView = usePerView();
  const maxIndex = Math.max(0, slides.length - perView);
  const [index, setIndex] = useState(0);

  useEffect(() => {
    setIndex((current) => Math.min(current, maxIndex));
  }, [maxIndex]);

  const goPrev = () => setIndex((i) => Math.max(0, i - 1));
  const goNext = () => setIndex((i) => Math.min(maxIndex, i + 1));

  const slidePercent = 100 / perView;

  return (
    <section className="relative overflow-x-hidden bg-white px-4 py-14 sm:px-6 sm:py-20">
      <div className="mx-auto max-w-6xl">
        <div className="mx-auto mb-8 max-w-2xl px-1 text-center sm:mb-10">
          <p className="mb-2 text-xs font-bold uppercase tracking-[0.14em] text-[#3451d1]">
            Fitur portal
          </p>
          <h2 className="lp-display text-xl font-semibold tracking-tight text-[#0f172a] sm:text-3xl">
            Dari pasang proyek sampai pantau kerja
          </h2>
        </div>

        <div className="relative">
          {/* Track — padding kiri/kanan agar panah tidak menimpa konten di HP */}
          <div className="overflow-hidden px-11 sm:px-10">
            <div
              className="flex transition-transform duration-500 ease-out"
              style={{ transform: `translateX(-${index * slidePercent}%)` }}
            >
              {slides.map((slide) => (
                <article
                  key={slide.title}
                  className="shrink-0 px-2 sm:px-4"
                  style={{ width: `${slidePercent}%` }}
                >
                  <div className="mx-auto flex h-full max-w-sm flex-col items-center text-center">
                    <div className="flex h-40 w-full max-w-full items-center justify-center sm:h-56">
                      <img
                        src={slide.image}
                        alt={slide.imageAlt}
                        className="max-h-full max-w-full w-auto object-contain"
                        loading="lazy"
                        decoding="async"
                      />
                    </div>
                    <h3 className="lp-display mt-4 text-base font-semibold text-[#0f172a] sm:mt-5 sm:text-lg">
                      {slide.title}
                    </h3>
                    <p className="mt-2 text-sm leading-relaxed text-slate-500">
                      {slide.body}
                    </p>
                  </div>
                </article>
              ))}
            </div>
          </div>

          {/* Arrows — selalu tampil (pola slider); disabled jika tidak bisa digeser */}
          <button
            type="button"
            onClick={goPrev}
            disabled={index === 0}
            aria-label="Sebelumnya"
            className="absolute left-0 top-[36%] z-10 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full border border-slate-200 bg-white text-[#3451d1] shadow-md transition hover:border-[#4A6CF7]/40 hover:bg-[#f5f8ff] disabled:cursor-not-allowed disabled:opacity-35 sm:top-[40%] sm:h-11 sm:w-11"
          >
            <ChevronLeft size={20} />
          </button>
          <button
            type="button"
            onClick={goNext}
            disabled={index === maxIndex}
            aria-label="Berikutnya"
            className="absolute right-0 top-[36%] z-10 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full border border-slate-200 bg-white text-[#3451d1] shadow-md transition hover:border-[#4A6CF7]/40 hover:bg-[#f5f8ff] disabled:cursor-not-allowed disabled:opacity-35 sm:top-[40%] sm:h-11 sm:w-11"
          >
            <ChevronRight size={20} />
          </button>
        </div>

        {/* Dots */}
        <div className="mt-8 flex items-center justify-center gap-2.5 sm:mt-10">
          {Array.from({ length: maxIndex + 1 }).map((_, i) => (
            <button
              key={i}
              type="button"
              aria-label={`Slide ${i + 1}`}
              onClick={() => setIndex(i)}
              className={
                i === index
                  ? 'h-2.5 w-2.5 rounded-full bg-[#3451d1]'
                  : 'h-2.5 w-2.5 rounded-full bg-slate-300 transition hover:bg-slate-400'
              }
            />
          ))}
        </div>
      </div>
    </section>
  );
}

export default function Landing() {
  const [navSolid, setNavSolid] = useState(false);

  useEffect(() => {
    const onScroll = () => {
      setNavSolid(window.scrollY > 48);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <div className="lp-root overflow-x-hidden bg-white text-[#0f172a]">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@500;600;700&family=Plus+Jakarta+Sans:wght@400;500;600;700&display=swap');
        .lp-root { font-family: 'Plus Jakarta Sans', system-ui, sans-serif; }
        .lp-display { font-family: 'Space Grotesk', system-ui, sans-serif; }
        .lp-grid-bg {
          background-color: #1e3a8a;
          background-image:
            radial-gradient(90% 70% at 100% 0%, rgba(74,108,247,0.35) 0%, transparent 55%),
            radial-gradient(70% 50% at 0% 100%, rgba(33,118,204,0.28) 0%, transparent 50%),
            linear-gradient(rgba(255,255,255,0.06) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.06) 1px, transparent 1px);
          background-size: auto, auto, 40px 40px, 40px 40px;
        }
      `}</style>

      {/* Nav overlay di atas hero — transparan di atas foto agar logo di gambar terlihat */}
      <nav
        className={[
          'fixed inset-x-0 top-0 z-50 transition-all duration-300',
          navSolid
            ? 'border-b border-white/10 bg-[#1e3a8a]/95 shadow-lg shadow-blue-950/20 backdrop-blur-md'
            : 'border-b border-transparent bg-transparent',
        ].join(' ')}
      >
        <div className="relative mx-auto flex w-full max-w-6xl items-center justify-between gap-3 px-4 py-3.5 sm:gap-4 sm:px-6 sm:py-5">
          <div
            className={[
              'flex min-w-0 items-center gap-2.5 transition-opacity duration-300 sm:gap-3',
              navSolid ? 'opacity-100' : 'opacity-0 pointer-events-none',
            ].join(' ')}
            aria-hidden={!navSolid}
          >
            <img
              src={logoApp}
              alt="Indoteknisi"
              className="h-7 w-7 shrink-0 rounded-md bg-white object-contain p-1 sm:h-8 sm:w-8"
            />
            <div className="min-w-0 leading-tight">
              <p className="lp-display truncate text-sm font-semibold tracking-tight text-white">
                Indoteknisi
              </p>
              <p className="text-[11px] text-blue-100/70">Portal Proyek</p>
            </div>
          </div>
          <div className="ml-auto flex shrink-0 items-center gap-3 sm:gap-6">
            <a
              href={MARKETPLACE_URL}
              className="hidden text-sm font-medium text-white/90 transition hover:text-white sm:inline"
            >
              Marketplace
            </a>
            <Link
              to="/login"
              className="inline-flex items-center gap-2 rounded-md border border-white/35 bg-white/10 px-3 py-1.5 text-sm font-semibold text-white backdrop-blur-sm transition hover:border-white/55 hover:bg-white/20 sm:px-4 sm:py-2"
            >
              <LogIn size={16} />
              Masuk
            </Link>
          </div>
        </div>
      </nav>

      {/* HERO full-bleed dari atas viewport — foto di belakang nav */}
      <header className="relative flex min-h-[100svh] flex-col overflow-hidden sm:min-h-screen">
        {/* Background: mobile portrait vs desktop */}
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat sm:hidden"
          style={{
            backgroundImage: `url(${heroImgMobile})`,
            backgroundPosition: 'center top',
          }}
          aria-hidden
        />
        <div
          className="absolute inset-0 hidden bg-cover bg-center bg-no-repeat sm:block"
          style={{
            backgroundImage: `url(${heroImg})`,
            backgroundPosition: 'center top',
          }}
          aria-hidden
        />

        {/* Scrim lebih ringan di kiri-atas agar logo di foto tetap kebaca */}
        <div
          className="pointer-events-none absolute inset-0 bg-gradient-to-b from-[#0f172a]/35 via-[#1e3a8a]/30 to-[#0f172a]/40"
          aria-hidden
        />
        <div
          className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#0f172a]/55 via-transparent to-transparent"
          aria-hidden
        />

        <div className="relative z-10 flex w-full flex-1 flex-col items-stretch justify-end pt-20 sm:pt-28">
          <div className="w-full rounded-t-[2.25rem] border border-b-0 border-white/15 bg-gradient-to-br from-[#1e3a8a]/95 via-[#3451d1]/92 to-[#2176CC]/90 px-4 py-7 text-center shadow-[0_-12px_40px_rgba(15,23,42,0.35)] backdrop-blur-md sm:rounded-t-[4.5rem] sm:px-10 sm:py-11 lg:rounded-t-[7rem]">
            <div className="mx-auto max-w-4xl">
              <h1 className="lp-display text-[1.35rem] font-semibold leading-[1.25] tracking-tight text-white sm:text-3xl sm:leading-[1.2] lg:text-[2.35rem]">
                Indoteknisi: dari pasang proyek sampai pantau kerja, satu portal.
              </h1>
            </div>
          </div>
        </div>
      </header>

      {/* Features slider */}
      <FeatureSlider slides={FEATURE_SLIDES} />

      {/* Owner vs Mitra */}
      <section className="grid grid-cols-1 lg:grid-cols-2 lg:min-h-screen">
        {/* Owner */}
        <div className="relative flex min-h-[20rem] items-center justify-center overflow-hidden px-4 py-12 sm:min-h-[26rem] sm:px-10 sm:py-16 lg:min-h-screen lg:px-14">
          <div
            className="pointer-events-none absolute inset-0 scale-105 bg-cover bg-center bg-no-repeat"
            style={{ backgroundImage: `url(${ownerbg})` }}
            aria-hidden
          />
          <div
            className="pointer-events-none absolute inset-0 bg-[#1e3a8a]/72"
            aria-hidden
          />
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-[#1e3a8a]/40 via-transparent to-[#1e3a8a]/55" aria-hidden />

          <div className="relative z-10 mx-auto w-full max-w-md rounded-2xl border border-white/15 bg-[#0f172a]/35 px-5 py-7 text-center shadow-xl shadow-blue-950/30 backdrop-blur-md sm:px-9 sm:py-10">
            <div className="mx-auto flex h-11 w-11 items-center justify-center rounded-md bg-white/15 text-white ring-1 ring-white/20">
              <User2Icon size={20} />
            </div>
            <h3 className="lp-display mt-5 text-xl font-semibold text-white drop-shadow-sm">
              Untuk Owner
            </h3>
            <p className="mt-3 text-sm leading-relaxed text-white/90">
              Anda pemilik proyek. Pasang kebutuhan kerja, terima penawaran mitra,
              lalu pilih yang paling cocok — semua di satu portal.
            </p>
            <ul className="mx-auto mt-6 w-fit max-w-full space-y-3 text-left">
              {[
                'Pasang dan kelola proyek',
                'Lihat penawaran dari mitra',
                'Chat & notifikasi di portal',
              ].map((item) => (
                <li
                  key={item}
                  className="flex items-center gap-2.5 text-sm text-white"
                >
                  <CheckCircle2 size={16} className="shrink-0 text-[#93c5fd]" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Mitra */}
        <div className="relative flex min-h-[20rem] items-center justify-center overflow-hidden px-4 py-12 sm:min-h-[26rem] sm:px-10 sm:py-16 lg:min-h-screen lg:px-14">
          <div
            className="pointer-events-none absolute inset-0 scale-105 bg-cover bg-center bg-no-repeat"
            style={{ backgroundImage: `url(${mitrabg})` }}
            aria-hidden
          />
          <div
            className="pointer-events-none absolute inset-0 bg-[#3451d1]/70"
            aria-hidden
          />
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-[#3451d1]/35 via-transparent to-[#1e3a8a]/45" aria-hidden />

          <div className="relative z-10 mx-auto w-full max-w-md rounded-2xl border border-white/15 bg-[#1e3a8a]/40 px-5 py-7 text-center shadow-xl shadow-blue-950/25 backdrop-blur-md sm:px-9 sm:py-10">
            <div className="mx-auto flex h-11 w-11 items-center justify-center rounded-md bg-white/15 text-white ring-1 ring-white/20">
              <HardHat size={20} />
            </div>
            <h3 className="lp-display mt-5 text-xl font-semibold text-white drop-shadow-sm">
              Untuk Mitra
            </h3>
            <p className="mt-3 text-sm leading-relaxed text-white/90">
              Anda pengerja proyek. Cari peluang yang cocok, kirim penawaran,
              lalu kerjakan dan pantau progres dari satu dashboard.
            </p>
            <ul className="mx-auto mt-6 w-fit max-w-full space-y-3 text-left">
              {[
                'Cari peluang proyek',
                'Kirim dan kelola penawaran',
                'Jalankan & pantau pekerjaan',
              ].map((item) => (
                <li
                  key={item}
                  className="flex items-center gap-2.5 text-sm font-medium text-white"
                >
                  <CheckCircle2 size={16} className="shrink-0 text-[#bfdbfe]" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Tiga langkah untuk mulai */}
      <section className="bg-[#f5f8ff] px-4 py-14 sm:px-6 sm:py-20">
        <div className="mx-auto max-w-6xl">
          <div className="max-w-2xl">
            <p className="mb-2 text-xs font-bold uppercase tracking-[0.14em] text-[#3451d1]">
              Cara kerja
            </p>
            <h2 className="lp-display text-xl font-semibold tracking-tight text-[#0f172a] sm:text-3xl">
              Tiga langkah untuk mulai
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-slate-500 sm:text-[15px]">
              Dari masuk akun hingga pantau pengerjaan — alur yang sama untuk owner dan mitra.
            </p>
          </div>

          <div className="mt-10 grid grid-cols-1 gap-5 sm:mt-12 sm:gap-6 md:grid-cols-3 md:gap-5">
            {STEPS.map((item) => (
              <article
                key={item.step}
                className="group flex h-full flex-col overflow-hidden rounded-2xl border border-blue-100/80 bg-white shadow-sm shadow-slate-200/50 transition duration-200 hover:-translate-y-1 hover:border-[#4A6CF7]/35 hover:shadow-md hover:shadow-blue-100/80"
              >
                <div className="relative flex aspect-[4/3] items-center justify-center bg-gradient-to-b from-[#eef3ff] to-[#f8faff] px-4 pt-5">
                  <span className="lp-display absolute left-4 top-4 z-10 inline-flex h-8 min-w-8 items-center justify-center rounded-lg bg-[#3451d1] px-2 text-sm font-semibold text-white shadow-sm">
                    {item.step}
                  </span>
                  <img
                    src={item.image}
                    alt={item.imageAlt}
                    className="h-full max-h-[160px] w-auto max-w-full object-contain drop-shadow-sm transition duration-200 group-hover:scale-[1.02] sm:max-h-[200px]"
                    loading="lazy"
                    decoding="async"
                  />
                </div>
                <div className="flex flex-1 flex-col px-5 pb-6 pt-4">
                  <h3 className="text-[15px] font-semibold leading-snug text-[#0f172a]">
                    {item.title}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-500">{item.body}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* CTA akhir — download apps + login web */}
      <section
        className="relative flex min-h-[24rem] items-center justify-center overflow-hidden bg-[#1e3b8a] px-4 py-14 sm:min-h-[28rem] sm:px-6 sm:py-20 lg:min-h-screen"
        style={{
          backgroundImage: `url(${ctaDownload})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="pointer-events-none absolute inset-0 bg-[#1e3a8a]/35" aria-hidden />

        <div className="relative z-10 mx-auto w-full max-w-3xl text-center">
          <p className="mb-3 text-xs font-bold uppercase tracking-[0.14em] text-blue-100/80">
            Mulai sekarang
          </p>
          <h2 className="lp-display text-xl font-semibold text-white sm:text-3xl lg:text-[2.5rem]">
            Download aplikasi atau masuk lewat web
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-sm leading-relaxed text-blue-50/85 sm:text-[15px]">
            Pantau proyek dari HP lewat aplikasi Indoteknisi. Atau masuk langsung ke Portal Proyek
            di browser dengan akun yang sama.
          </p>

          <div className="mt-8 flex w-full flex-col items-stretch justify-center gap-3 sm:mt-9 sm:flex-row sm:flex-wrap sm:items-center sm:gap-4">
            <a
              href={PLAY_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex w-full items-center justify-center gap-2.5 rounded-xl bg-black px-5 py-3 text-white shadow-lg transition hover:bg-slate-900 sm:w-auto sm:min-w-[11.5rem]"
            >
              <svg viewBox="0 0 24 24" className="h-6 w-6 shrink-0" aria-hidden>
                <path
                  fill="currentColor"
                  d="M3.6 2.5c.3-.3.7-.4 1.1-.2l13.2 7.5c.5.3.5 1 0 1.3L4.7 18.6c-.4.2-.8.1-1.1-.2-.2-.2-.3-.5-.3-.8V3.3c0-.3.1-.6.3-.8Zm14.5 9.3 2.4 1.4c.5.3.5 1 0 1.3l-2.1 1.2-3.1-3.1 2.8-.8Zm-4.5.7 3.4 3.4-9.7 5.5c-.2.1-.4.1-.6 0l6.9-8.9Z"
                />
              </svg>
              <span className="text-left leading-tight">
                <span className="block text-[10px] font-medium opacity-80">GET IT ON</span>
                <span className="block text-sm font-bold tracking-wide">Google Play</span>
              </span>
            </a>

            <a
              href={APP_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex w-full items-center justify-center gap-2.5 rounded-xl bg-black px-5 py-3 text-white shadow-lg transition hover:bg-slate-900 sm:w-auto sm:min-w-[11.5rem]"
            >
              <svg viewBox="0 0 24 24" className="h-6 w-6 shrink-0" aria-hidden>
                <path
                  fill="currentColor"
                  d="M16.4 12.6c0-2.1 1.7-3.1 1.8-3.2-1-1.4-2.5-1.6-3-1.7-1.3-.1-2.5.8-3.1.8-.7 0-1.7-.7-2.8-.7-1.4 0-2.8.9-3.5 2.2-1.5 2.6-.4 6.4 1.1 8.5.7 1 1.6 2.2 2.7 2.1 1.1-.1 1.5-.7 2.8-.7s1.7.7 2.8.7c1.2 0 1.9-1 2.6-2 .8-1.1 1.1-2.2 1.1-2.3-.1 0-2.2-.8-2.2-3.5ZM14.5 6.5c.6-.7 1-1.7.9-2.7-0.9.1-1.9.6-2.5 1.3-.6.6-1.1 1.6-1 2.6 1 .1 1.9-.5 2.6-1.2Z"
                />
              </svg>
              <span className="text-left leading-tight">
                <span className="block text-[10px] font-medium opacity-80">Download on the</span>
                <span className="block text-sm font-bold tracking-wide">App Store</span>
              </span>
            </a>

            <Link
              to="/login"
              className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-white px-5 py-3.5 text-sm font-bold text-[#3451d1] shadow-lg transition hover:bg-blue-50 sm:w-auto sm:min-w-[11.5rem]"
            >
              <LogIn size={18} />
              Login Web
            </Link>
          </div>

          <p className="mt-5 text-xs text-blue-100/60">
            Satu akun Indoteknisi untuk aplikasi dan Portal Proyek.
          </p>
        </div>
      </section>

      <footer className="border-t border-blue-950/40 bg-[#0f172a] text-slate-300">
        <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6 sm:py-14">
          <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-4 lg:gap-8">
            <div className="sm:col-span-2 lg:col-span-1">
              <div className="flex items-center gap-3">
                <img
                  src={logoApp}
                  alt="Indoteknisi"
                  className="h-10 w-10 rounded-lg bg-white object-contain p-1"
                />
                <div>
                  <p className="lp-display text-base font-semibold text-white">Indoteknisi</p>
                  <p className="text-xs text-slate-400">Portal Proyek</p>
                </div>
              </div>
              <p className="mt-4 max-w-xs text-sm leading-relaxed text-slate-400">
                Portal untuk memasang proyek, mengelola penawaran mitra, dan memantau
                pengerjaan — terhubung dengan akun Indoteknisi.
              </p>
            </div>

            <div>
              <p className="text-xs font-bold uppercase tracking-[0.14em] text-slate-500">
                Portal
              </p>
              <ul className="mt-4 space-y-2.5 text-sm">
                <li>
                  <Link to="/login" className="transition hover:text-white">
                    Masuk Portal Proyek
                  </Link>
                </li>
                <li>
                  <a href={MARKETPLACE_URL} className="transition hover:text-white">
                    Marketplace Indoteknisi
                  </a>
                </li>
              </ul>
            </div>

            <div>
              <p className="text-xs font-bold uppercase tracking-[0.14em] text-slate-500">
                Aplikasi
              </p>
              <ul className="mt-4 space-y-2.5 text-sm">
                <li>
                  <a
                    href={PLAY_STORE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="transition hover:text-white"
                  >
                    Google Play
                  </a>
                </li>
                <li>
                  <a
                    href={APP_STORE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="transition hover:text-white"
                  >
                    App Store
                  </a>
                </li>
              </ul>
            </div>

            <div>
              <p className="text-xs font-bold uppercase tracking-[0.14em] text-slate-500">
                Untuk siapa
              </p>
              <ul className="mt-4 space-y-2.5 text-sm text-slate-400">
                <li>Owner — pasang & pilih mitra</li>
                <li>Mitra — bid & kerjakan proyek</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="border-t border-white/10">
          <div className="mx-auto flex max-w-6xl flex-col gap-2 px-6 py-5 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-xs text-slate-500">
              © {new Date().getFullYear()} Indoteknisi. Semua hak dilindungi.
            </p>
            <p className="text-xs text-slate-500">
              Bagian dari ekosistem{' '}
              <a href={MARKETPLACE_URL} className="text-slate-400 transition hover:text-white">
                indoteknisi.net
              </a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
